import React, { useState } from 'react';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import EditIcon from '@grnhse/seedling/lib/birch/icons/edit';
import {
  ExpandableButton,
  PrimaryButton,
  SecondaryButton,
  TertiaryButton,
} from '@grnhse/seedling/lib/birch/components/buttons';
import {
  ExpandableMenu,
  MenuButton,
  MenuList,
  MenuItem,
} from '@grnhse/seedling/lib/birch/components/expandable_menu';
import { useModal } from '@grnhse/seedling/lib/birch/components/modals';
import DataEntryModal from '@grnhse/seedling/lib/birch/components/modals/data_entry_modal';
import { SimpleSingleDropdown } from '@grnhse/seedling/lib/birch/components/forms/dropdown';
import { TextInput } from '@grnhse/seedling/lib/birch/components/forms/text_input';
import { useUserContext } from 'talent_matching/contexts/user_context';
import { Item } from '@grnhse/seedling/lib/birch/components/forms/dropdown';
import {
  createSavedSearch,
  fetchSavedSearch,
  updateSavedSearch,
} from 'talent_matching/api';
import {
  extractFormFieldsFromSavedSearch,
  toSavedSearchQueryString,
} from 'talent_matching/components/filters/utils';
import { useFormContext } from '@grnhse/seedling/lib/birch/components/forms/form';
import styled from 'styled-components';
import AdvancedFilter from 'talent_matching/components/filters/advanced_filter';
import { withLocalePrefix } from 'shared/utils/translation';
import { AdvancedFilterType } from '../../types';
import { FORM_DEFAULTS } from 'talent_matching/components/filter_form';

const t = withLocalePrefix('talent_matching.filter_row.saved_filters');

type Props = {
  onClearAll: () => void;
};

const NoWrapTertiaryButton = styled(TertiaryButton)`
  white-space: nowrap;
`;

const NoWrapSecondaryButton = styled(SecondaryButton)`
  white-space: nowrap;
`;

const StyledMenuButton = styled(MenuButton)`
  white-space: nowrap;
`;

const SavedFilters = ({ onClearAll }: Props) => {
  const { savedSearchOptions, setSavedSearchOptions, paths } = useUserContext();
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [selectedFilterGroup, setSelectedFilterGroup] = useState<Item | null>(
    null
  );

  const [loadFiltersModalOpen, openLoadFiltersModal, closeLoadFiltersModal] =
    useModal();
  const [saveFiltersModalOpen, openSaveFiltersModal, closeSaveFiltersModal] =
    useModal();

  const { getValues, setValue, watch, reset } = useFormContext();
  const filterGroup = watch('filterGroup');

  const makeQueryString = (): string => {
    const formValues = getValues();
    formValues.advancedFilters = formValues.advancedFilters.filter(
      (filter: AdvancedFilterType) => new AdvancedFilter(filter).applied()
    );
    formValues.educationFilters = (formValues.educationFilters || []).filter(
      (filter: AdvancedFilterType) => new AdvancedFilter(filter).applied()
    );
    return toSavedSearchQueryString(formValues);
  };

  const onApplyFilterSet = async () => {
    if (!selectedFilterGroup) {
      onClearAll();
      closeLoadFiltersModal();
      return;
    }

    const response = await fetchSavedSearch(paths, selectedFilterGroup);
    const formFields = extractFormFieldsFromSavedSearch(response);

    // Clear the form before applying new filters
    reset(FORM_DEFAULTS);

    Object.keys(formFields).forEach((key) => {
      setValue(key, formFields[key], { shouldDirty: true });
    });

    setValue('filterGroup', selectedFilterGroup);
    closeLoadFiltersModal();
  };

  const onSave = async () => {
    try {
      const response = await createSavedSearch(paths, makeQueryString(), name);

      if (response) {
        const newSavedSearch: Item = {
          value: response.id.toString(),
          label: response.label,
        };
        setSavedSearchOptions([newSavedSearch, ...savedSearchOptions]);
        setError('');
        setValue('filterGroup', newSavedSearch);
      }

      setName('');
      closeSaveFiltersModal();
    } catch (_error) {
      setError(t('name_taken_error'));
    }
  };

  const onUpdate = async () => {
    if (!selectedFilterGroup) {
      return;
    }

    await updateSavedSearch(paths, selectedFilterGroup, makeQueryString());

    closeSaveFiltersModal();
  };

  const onOpenLoadFiltersModal = () => {
    setSelectedFilterGroup(filterGroup);
    openLoadFiltersModal();
  };

  const onCloseSaveFiltersModal = () => {
    setError('');
    setName('');
    closeSaveFiltersModal();
  };

  return (
    <Stack direction="row" spacing={2} alignContent="flex-end">
      {filterGroup ? (
        <Stack alignContent="flex-start" sx={{ paddingTop: 1 }}>
          <NoWrapTertiaryButton
            size="md"
            onClick={onOpenLoadFiltersModal}
            icon={EditIcon}
            placement="right"
          >
            {filterGroup.label}
          </NoWrapTertiaryButton>
        </Stack>
      ) : (
        <Stack alignContent="flex-start" sx={{ paddingTop: 1 }}>
          <NoWrapTertiaryButton size="md" onClick={onOpenLoadFiltersModal}>
            {t('load')}
          </NoWrapTertiaryButton>
        </Stack>
      )}
      <Stack direction="row">
        {filterGroup ? (
          <ExpandableMenu>
            <ExpandableButton as={StyledMenuButton} size="md">
              {t('save_filters')}
            </ExpandableButton>
            <MenuList>
              <MenuItem onSelect={onUpdate}>
                {t('save_changes', { name: filterGroup.label })}
              </MenuItem>
              <MenuItem onSelect={openSaveFiltersModal}>
                {t('save_as')}
              </MenuItem>
            </MenuList>
          </ExpandableMenu>
        ) : (
          <NoWrapSecondaryButton size="md" onClick={openSaveFiltersModal}>
            {t('save_filters')}
          </NoWrapSecondaryButton>
        )}
      </Stack>

      {/* Load Filters Modal */}
      <DataEntryModal
        size="medium"
        isOpen={loadFiltersModalOpen}
        onDismiss={closeLoadFiltersModal}
        ariaLabel={t('load_filter_aria_label')}
      >
        <DataEntryModal.Header onClick={closeLoadFiltersModal}>
          {t('load')}
        </DataEntryModal.Header>
        <DataEntryModal.Body>
          <SimpleSingleDropdown
            selectedItem={selectedFilterGroup}
            items={savedSearchOptions}
            onSelectedItemChange={setSelectedFilterGroup}
            ariaLabel={t('load_label')}
          />
        </DataEntryModal.Body>
        <DataEntryModal.Footer>
          <Stack direction="row" justifyContent="flex-end">
            <SecondaryButton onClick={closeLoadFiltersModal}>
              {t('cancel')}
            </SecondaryButton>
            <PrimaryButton onClick={onApplyFilterSet}>
              {t('apply_filters')}
            </PrimaryButton>
          </Stack>
        </DataEntryModal.Footer>
      </DataEntryModal>

      {/* Save Filters Modal */}
      <DataEntryModal
        size="medium"
        isOpen={saveFiltersModalOpen}
        onDismiss={onCloseSaveFiltersModal}
        ariaLabel={t('save_filter_aria_label')}
      >
        <DataEntryModal.Header onClick={onCloseSaveFiltersModal}>
          {t('save_filters')}
        </DataEntryModal.Header>
        <DataEntryModal.Body>
          <Stack sx={{ marginBottom: 2 }}>
            <TextInput
              value={name}
              onChange={(e) => setName(e.currentTarget.value)}
              label={t('save_filters')}
              error={!!error}
              helperText={error}
            />
          </Stack>{' '}
        </DataEntryModal.Body>
        <DataEntryModal.Footer>
          <Stack direction="row" justifyContent="flex-end">
            <SecondaryButton onClick={onCloseSaveFiltersModal}>
              {t('cancel')}
            </SecondaryButton>
            <PrimaryButton onClick={onSave} disabled={!name}>
              {t('save')}
            </PrimaryButton>
          </Stack>
        </DataEntryModal.Footer>
      </DataEntryModal>
    </Stack>
  );
};

export default SavedFilters;
